import moment from 'moment/moment';

export const isMoreThanOneHour = (from: number, to: number) => {
  const diffMs = Math.abs(to - from);
  const oneHourMs = 60 * 60 * 1000;

  return diffMs > oneHourMs;
};

export const timestampToISO8601UTC = (timestamp: string | number) => {
  return moment(Number(timestamp)).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
};

export const DAILY_FORMAT = 'MMM DD';
export const HOURLY_FORMAT = 'MMM DD HH:mm';

// Keep in sync with cloud-ui/modules/autoops-lib/util.ts
const CHART_TIME_ZONES = ['utc', 'local'] as const;

export type ChartTimeZone = (typeof CHART_TIME_ZONES)[number];

// Local time by default, like the rest of the UI; UTC is an opt-in for users
// who want the charts to line up with Billing.
export const DEFAULT_CHART_TIME_ZONE: ChartTimeZone = 'local';

export const isChartTimeZone = (value: unknown): value is ChartTimeZone =>
  CHART_TIME_ZONES.includes(value as ChartTimeZone);

// Elastic Charts derives the multilayer time axis from the series `timeZone`
// and ignores the axis `tickFormat`, so every series on a page has to read the
// same preference or the cursor line stops lining up with the tooltip.
// `undefined` leaves the series on the browser zone.
export const getSeriesTimeZone = (timeZone: ChartTimeZone) =>
  timeZone === 'utc' ? 'UTC' : undefined;

export const utcTimeFormatter =
  (format: string): ((value: number) => string) =>
  (value: number): string =>
    moment.utc(value).format(format);

const localTimeFormatter =
  (format: string): ((value: number) => string) =>
  (value: number): string =>
    moment(value).format(format);

export const formatChartAxisTick = (format: string, timeZone: ChartTimeZone) =>
  timeZone === 'utc' ? utcTimeFormatter(format) : localTimeFormatter(format);

export const formatChartTimestamp = (
  format: string,
  timeZone: ChartTimeZone
) =>
  timeZone === 'utc'
    ? (value: number): string => `${utcTimeFormatter(format)(value)} (UTC)`
    : localTimeFormatter(format);

export const formatTimestamp = (timestamp?: number): string => {
  if (!timestamp) return '-';

  const date = new Date(timestamp);

  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = date.getMilliseconds();

  const formattedTime = `${month} ${day}, ${year} @ ${hours}:${minutes}:${seconds}`;
  return milliseconds > 0
    ? `${formattedTime}.${String(milliseconds).padStart(3, '0')}`
    : formattedTime;
};

const NOTIFICATION_TIMESTAMP_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

const notificationsTimestampFormatter = new Intl.DateTimeFormat(
  'en-US',
  NOTIFICATION_TIMESTAMP_FORMAT_OPTIONS
);

export const formatNotificationTimestamp = (timestamp?: number) => {
  if (!timestamp) return '-';
  return notificationsTimestampFormatter.format(new Date(timestamp));
};
